import { useCallback, useEffect, useRef, useState } from "react";

export function useVideoPlayer({
  onFinish,
}: {
  lockInteraction?: (duration?: number) => boolean;
  onFinish?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const initialPlayAttempted = useRef(false);

  // Initial autoplay attempt
  useEffect(() => {
    const video = videoRef.current;
    if (!video || initialPlayAttempted.current) return;

    let cancelled = false;

    const tryPlay = async () => {
      initialPlayAttempted.current = true;
      try {
        video.muted = false;
        await video.play();
        if (!cancelled) {
          setMuted(false);
          setShowPlayButton(false);
        }
      } catch {
        console.log("Autoplay with sound blocked, playing muted");
        video.muted = true;
        if (!cancelled) setMuted(true);
        try {
          await video.play();
          if (!cancelled) setShowPlayButton(false);
        } catch (err) {
          if (!cancelled) console.error("Video play failed:", err);
          console.log("Autoplay completely blocked, showing play button");
          if (!cancelled) setShowPlayButton(true);
        }
      }
    };

    tryPlay();

    return () => {
      cancelled = true;
    };
  }, []);

  // Ensure mute state is reflected after interaction
  useEffect(() => {
    const video = videoRef.current;
    if (video && hasInteracted) {
      video.muted = muted;
    }
  }, [muted, hasInteracted]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    setHasInteracted(true);

    const newMuted = !muted;
    setMuted(newMuted);
    video.muted = newMuted;

    if (!newMuted && video.paused) {
      video.play().catch((err) => {
        console.warn("Play failed on unmute:", err);
      });
    }
  }, [muted]);

  const handlePlayButtonClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    setHasInteracted(true);
    setShowPlayButton(false);

    // Try to play with sound first
    video.muted = false;
    setMuted(false);

    video.play().catch(() => {
      // If that fails, fall back to muted
      video.muted = true;
      setMuted(true);
      video.play().catch((err) => {
        console.warn("Play failed even after user interaction:", err);
      });
    });
  }, []);

  const handleInteraction = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      const isInteractiveElement =
        target.closest("button") || target.closest('[role="button"]');

      if (!isInteractiveElement && videoRef.current) {
        const video = videoRef.current;
        setHasInteracted(true);

        if (video.paused) {
          video.muted = false;
          setMuted(false);
          video.play().catch(() => {
            video.muted = true;
            setMuted(true);
            video
              .play()
              .catch((err) =>
                console.warn("Playback failed on interaction:", err),
              );
          });
        } else {
          const newMuted = !muted;
          video.muted = newMuted;
          setMuted(newMuted);
        }
      }
    },
    [muted],
  );

  const handleSkip = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (videoRef.current) {
        videoRef.current.pause();
      }
      onFinish?.();
    },
    [onFinish],
  );

  return {
    videoRef,
    muted,
    showPlayButton,
    toggleMute,
    handleInteraction,
    handleSkip,
    handlePlayButtonClick,
  };
}
